/**
 * WmsErrorBoundary
 *
 * A throw inside any WMS page used to unmount the whole admin shell — a blank white
 * page with no sidebar and no indication of what went wrong. React has no default
 * recovery: one uncaught render error tears down the entire tree.
 *
 * This boundary contains the damage to the WMS area and, crucially, shows WHAT failed
 * instead of showing nothing. In dev it prints the message and component stack; in
 * production it shows a recoverable message with the details behind a disclosure.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown in the heading so the user knows which area failed. */
  area?: string;
}

interface State {
  error: Error | null;
  componentStack: string;
}

export default class WmsErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info?.componentStack || '' });
    // Keep the raw error in the console too — the stack there has source-mapped frames.
    console.error('[WMS] render error:', error, info?.componentStack);
  }

  private reset = () => this.setState({ error: null, componentStack: '' });

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="p-6">
        <div className="mx-auto max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6">
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-2xl text-red-600" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-red-800">
                {this.props.area || 'This page'} failed to load
              </h2>
              <p className="mt-1 text-sm text-red-700">
                The rest of the application is unaffected — use the sidebar to continue, or
                try again.
              </p>

              <pre className="mt-4 overflow-x-auto whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-xs text-red-900">
{String(error?.message || error)}
              </pre>

              {componentStack ? (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-medium text-red-700">
                    Component stack
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-red-900">
{componentStack.trim()}
                  </pre>
                </details>
              ) : null}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={this.reset}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  Try again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Reload
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
